import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 
  'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 
  'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const CreateClient = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    state_of_residence: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // 1. Validation
    if (!profile || (profile.role !== 'advisor_admin' && profile.role !== 'advisor_user')) {
      setError("Unauthorized: Only advisors can create clients.");
      setLoading(false);
      return;
    }

    if (!profile.advisor_firm_id) {
      setError("Your advisor profile is incomplete (missing firm link). Please contact support.");
      setLoading(false);
      return;
    }

    try {
      // 2. Supabase Insert
      const { data, error: insertError } = await supabase
        .from('clients')
        .insert([{
          advisor_firm_id: profile.advisor_firm_id,
          advisor_user_id: profile.id,
          user_id: null, // No auth user yet
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone || null,
          state_of_residence: formData.state_of_residence,
          invitation_status: 'not_invited'
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      setSuccess(true);
      console.log('Client created successfully:', data);
      
      // Auto-navigate back after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);

    } catch (err) {
      console.error('Error creating client:', err);
      setError(err.message || "Failed to create client record.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto mt-12 text-center p-12 bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="flex justify-center mb-4">
          <div className="bg-green-100 p-3 rounded-full">
            <SafeIcon icon={FiIcons.FiCheck} className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-slate-900">Client Created!</h2>
        <p className="text-slate-600 mt-2">The client has been added to your firm's database.</p>
        <p className="text-slate-400 text-sm mt-4 italic">Redirecting to dashboard...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center mb-8">
        <button 
          onClick={() => navigate(-1)}
          className="mr-4 p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <SafeIcon icon={FiIcons.FiArrowLeft} className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Add New Client</h1>
          <p className="text-sm text-slate-500">Record basic information for a new estate planning prospect.</p>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-xl border border-slate-200 overflow-hidden">
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
              <div className="flex">
                <SafeIcon icon={FiIcons.FiAlertCircle} className="h-5 w-5 text-red-400 mr-2" />
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
              <input
                name="first_name"
                type="text"
                required
                value={formData.first_name}
                onChange={handleChange}
                className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
                placeholder="John"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
              <input
                name="last_name"
                type="text"
                required
                value={formData.last_name}
                onChange={handleChange}
                className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email Address *</label>
              <input
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
                placeholder="john.doe@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
              <input
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none"
                placeholder="(555) 000-0000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">State of Residence *</label>
            <select
              name="state_of_residence"
              required
              value={formData.state_of_residence}
              onChange={handleChange}
              className="block w-full sm:text-sm border-slate-300 rounded-md py-2 px-3 border focus:ring-slate-500 focus:border-slate-500 outline-none bg-white"
            >
              <option value="">Select a state</option>
              {STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          <div className="pt-4 border-t border-slate-100 flex justify-end items-center space-x-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-slate-800 hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <>
                  <SafeIcon icon={FiIcons.FiLoader} className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Saving...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiIcons.FiSave} className="-ml-1 mr-2 h-4 w-4" />
                  Save Client
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      
      <div className="mt-6 text-xs text-slate-400 italic text-center">
        * Required fields. Adding a client does not yet notify them or create a login.
      </div>
    </div>
  );
};

export default CreateClient;